import { useInfiniteQuery } from '@tanstack/react-query';
import { listConversationMessages } from '@/api/conversations';
import { queryKeys } from '@/lib/query-keys';
import type { CursorMensajes, ListaMensajes, MensajeChat } from '@/types/conversations';
import { INBOX_REFRESH_MS, MENSAJES_POR_PAGINA } from './inbox-format';

/**
 * Historial de una conversación, paginado por CURSOR (módulo 5).
 *
 * Es la primera query **infinita** del panel y por eso está sola en su archivo: el resto de
 * listados usan `limite`/`offset` (una página y ya), pero un chat crece sin tope y, si se paginara
 * por offset, cada mensaje nuevo desplazaría la página y el usuario acabaría viendo huecos o
 * duplicados. El backend da `siguienteCursor` (`antes`/`antesId`) y aquí se guarda como
 * `getNextPageParam`: "cargar mensajes anteriores" es `fetchNextPage()`.
 *
 * Orden: cada página llega ASCENDENTE, pero la página 0 es la MÁS NUEVA. Para pintar el chat de
 * arriba abajo se invierten las páginas antes de concatenarlas (`mensajes`).
 */

export function useConversationMessages(tenantId: string | null, conversationId: string | null) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.conversations.messages(tenantId ?? '', conversationId ?? ''),
    queryFn: ({ pageParam }) =>
      listConversationMessages(tenantId ?? '', conversationId ?? '', {
        limite: MENSAJES_POR_PAGINA,
        // `pageParam` es el cursor de la página anterior; `null` = la primera (los últimos N).
        antes: pageParam?.antes,
        antesId: pageParam?.antesId,
      }),
    initialPageParam: null as CursorMensajes | null,
    getNextPageParam: (lastPage: ListaMensajes) => lastPage.siguienteCursor ?? undefined,
    enabled: Boolean(tenantId && conversationId),
    /**
     * Se refresca solo, pero SOLO mientras haya una página cargada: TanStack relee las páginas ya
     * cargadas una por una y, con el historial largo hacia atrás, eso multiplicaría las peticiones
     * por cada intervalo. A partir de la segunda página el refresco es a mano (botón "Refrescar").
     */
    refetchInterval: (query) =>
      (query.state.data?.pages.length ?? 0) > 1 ? false : INBOX_REFRESH_MS,
  });

  const paginas = query.data?.pages ?? [];

  /** Todo el historial cargado, cronológico ascendente. */
  const mensajes: MensajeChat[] = [...paginas].reverse().flatMap((pagina) => pagina.mensajes);

  return {
    ...query,
    mensajes,
    /** Total de mensajes de la conversación (el backend lo manda en cada página). */
    total: paginas[0]?.total ?? 0,
  };
}
